import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { GLAccount } from '../types/business';
import { LiveGlAccount } from '../types/businessApi';
import { glService } from '../services/glService';
import { businessGlService } from '../services/businessGlService';
import { useBusinessDataMode } from '../hooks/useBusinessDataMode';
import { useBusinessOptional } from './BusinessContext';
import { isReadRestrictedRole } from '../components/business/settings/roleCopy';

// ============================================
// Types
// ============================================

interface BusinessAccountingContextValue {
  /** Preview-mode (mock) accounts — empty in live mode. */
  glAccounts: GLAccount[];
  /** Live-mode accounts from the accounting API — empty in preview mode. */
  liveAccounts: LiveGlAccount[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

// ============================================
// Context
// ============================================

// Exported so a story or test can supply a literal value without standing up a
// business (the provider itself needs BusinessProvider + a data mode).
export const BusinessAccountingContext = createContext<BusinessAccountingContextValue | null>(null);

// ============================================
// Provider
// ============================================

// Sources the Chart of Accounts from the mock glService in preview mode, or
// from the live accounting API (scoped to the active business) in live mode.
export const BusinessAccountingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const mode = useBusinessDataMode();
  const business = useBusinessOptional();
  const businessId = business?.currentBusiness?.id ?? null;
  // The chart of accounts is the books (PRD 89 §4.9): a field role is refused
  // it with 403 role_forbidden, on every live page this provider wraps. Not
  // asking keeps that refusal off pages the role may open; the accounts they
  // would feed (income account labels, GL pickers) are not on those pages.
  const readRestricted = isReadRestrictedRole(business?.currentBusiness?.role);

  const [glAccounts, setGlAccounts] = useState<GLAccount[]>([]);
  const [liveAccounts, setLiveAccounts] = useState<LiveGlAccount[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAccounts = useCallback(async () => {
    if (mode === 'live' && (!businessId || readRestricted)) {
      setLiveAccounts([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      if (mode === 'live') {
        const accounts = await businessGlService.listAccounts();
        setLiveAccounts(accounts);
      } else {
        const accounts = await glService.getChartOfAccounts();
        setGlAccounts(accounts);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load accounts');
    } finally {
      setIsLoading(false);
    }
  }, [mode, businessId, readRestricted]);

  const refresh = useCallback(async () => {
    await fetchAccounts();
  }, [fetchAccounts]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  return (
    <BusinessAccountingContext.Provider
      value={{ glAccounts, liveAccounts, isLoading, error, refresh }}
    >
      {children}
    </BusinessAccountingContext.Provider>
  );
};

// ============================================
// Hook
// ============================================

export function useBusinessAccounting(): BusinessAccountingContextValue {
  const ctx = useContext(BusinessAccountingContext);
  if (!ctx) {
    throw new Error(
      'useBusinessAccounting must be used within a BusinessAccountingProvider'
    );
  }
  return ctx;
}
